import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, resolveImg } from "@/App";
import SafeImg from "@/components/SafeImg";
import Seo from "@/components/Seo";

export default function Media() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // {image, name, subtitle}

  useEffect(() => {
    axios.get(`${API}/media`).then((r) => {
      setItems(r.data.filter((x) => x.media_image));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  // Close lightbox with Escape
  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => { if (e.key === "Escape") setLightbox(null); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox]);

  return (
    <div data-testid="media-page">
      <Seo
        title="En los medios"
        description="Apariciones en prensa y medios de comunicación de los invitados que han pasado por la Tertulia In Dubio de Sevilla. Archivo de recortes editoriales desde 2014."
        path="/en-los-medios"
      />
      {/* Hero with blue band */}
      <section
        className="relative py-14 md:py-24"
        style={{ background: "linear-gradient(135deg, #2c6b8f 0%, #3f7a99 45%, #5a91ab 100%)", color: "#fff" }}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-end justify-between gap-8 flex-wrap">
          <div>
            <span className="text-[11px] tracking-[0.3em] uppercase text-[rgba(255,255,255,0.7)]">Archivo de prensa</span>
            <h1 className="font-serif text-6xl md:text-7xl mt-3 leading-none">En los medios.</h1>
            <p className="mt-4 max-w-xl text-[rgba(255,255,255,0.78)] text-[15px] font-light leading-relaxed">
              La Tertulia In Dubio ha aparecido en ABC Sevilla, Diario de Sevilla y otros medios nacionales. Una selección de las apariciones de nuestros invitados recogidas por la prensa. Haz click en cualquier recorte para verlo ampliado.
            </p>
          </div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-[rgba(255,255,255,0.7)]" data-testid="media-count">
            {items.length} apariciones
          </div>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-20">
        {loading ? (
          <p className="text-[var(--text-muted)]">Cargando…</p>
        ) : (
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-16">
            {items.map((g, i) => (
              <article
                key={g.id}
                className="anim-fadeup group"
                style={{ animationDelay: `${Math.min(i * 0.04, 0.4)}s` }}
                data-testid={`media-item-${g.slug}`}
              >
                <button
                  type="button"
                  onClick={() => setLightbox({ image: g.media_image, name: g.name, subtitle: g.role || g.media_subtitle })}
                  className="block w-full text-left cursor-zoom-in"
                  data-testid={`media-open-${g.slug}`}
                  aria-label={`Ampliar recorte de ${g.name}`}
                >
                  <div className="relative overflow-hidden bg-white p-3 border border-[var(--line)] shadow-[0_14px_40px_-20px_rgba(22,40,60,0.28)] group-hover:shadow-[0_30px_70px_-25px_rgba(22,40,60,0.35)] transition-all duration-500">
                    <SafeImg
                      src={g.media_image}
                      alt={`${g.name} en los medios`}
                      name={g.name}
                      className="w-full h-auto transition-transform duration-700 group-hover:scale-[1.02]"
                    />
                    <span className="absolute top-4 right-4 px-2 py-1 bg-white/90 text-[9px] uppercase tracking-[0.22em] text-[var(--text-ink)] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      ⤢ Ampliar
                    </span>
                  </div>
                </button>
                <div className="mt-5">
                  <Link
                    to={`/invitados/${g.slug}`}
                    className="font-serif text-3xl leading-tight uppercase tracking-[0.05em] hover:text-[var(--gold-deep)] transition-colors"
                    data-testid={`media-name-${g.slug}`}
                  >
                    {g.name}
                  </Link>
                  {/* Short subtitle = short role (no URLs) */}
                  <p className="text-[11.5px] uppercase tracking-[0.22em] text-[var(--text-muted)] mt-2" data-testid={`media-subtitle-${g.slug}`}>
                    {(g.role || g.media_subtitle || "").slice(0, 110)}
                  </p>
                  <Link
                    to={`/invitados/${g.slug}`}
                    className="inline-block mt-3 text-[11px] uppercase tracking-[0.2em] border-b border-[var(--gold)] pb-0.5 hover:text-[var(--gold-deep)]"
                  >
                    Ver ficha →
                  </Link>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[300] bg-black/88 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 animate-[fadeIn_0.3s_ease]"
          onClick={() => setLightbox(null)}
          data-testid="media-lightbox"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 md:top-8 md:right-8 text-white/80 hover:text-white text-3xl leading-none w-12 h-12 flex items-center justify-center border border-white/30 rounded-full"
            aria-label="Cerrar"
            data-testid="lightbox-close"
          >
            ×
          </button>
          <div className="max-w-6xl max-h-[90vh] w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
            <img
              src={resolveImg(lightbox.image)}
              alt={lightbox.name}
              className="max-w-full max-h-[78vh] object-contain shadow-[0_40px_100px_-30px_rgba(0,0,0,0.8)]"
            />
            <div className="mt-5 text-center">
              <h2 className="font-serif text-3xl md:text-4xl text-white leading-tight uppercase tracking-[0.05em]">{lightbox.name}</h2>
              {lightbox.subtitle && (
                <p className="text-[12px] uppercase tracking-[0.22em] text-white/70 mt-2 max-w-2xl mx-auto">
                  {lightbox.subtitle.slice(0, 150)}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
